import * as Tone from 'tone';
import units from "../units.json"
import { getAudioGraphInstance } from "../audioEngine";
import { addKeysAsField, createStickySelector } from '../utils/misc';
import { staticSelect } from '../utils/react';
import { capitalizeFirstLetter, lowercaseFirstLetter } from '../utils/string';
import store, { setInstrumentParameter, setPresetParameter, setClipSequence, selectIsAnyInstrumentSolo, selectPlaybackMode, selectScenes, selectlaunchedSlots, selectPresets, makePreset, loadPresetInInstrument, setClipParameter, selectInstruments } from '../store';
import { createSelector } from '@reduxjs/toolkit';
import { FXWrapper } from '../fx/FXWrapper';
import { GrufBus } from '../GrufBus';
import colors from "../../styles/colors.module.scss";
import { ParameterHandler } from '../ParameterHandler';
import { GrufEQ3 } from '../3rd_party/GrufEQ3';
import { makeUID } from '../utils/math';
import { Sound } from '../../datatypes';

export class InstrumentBase {
    static metadata = {
        tipus: 'base',
        tipusDisplay: 'Base Instrument',
        versio: '0.0',
        descripcio: "El millor instrument de la història dels instruments, el més versàtil i el que tot ho pot fer. És tan bo que no té ni un so propi, perquè pot ser qualsevol so que vulguis!",
        color: colors.grey,
        hasLogo: false, // If set to true, a corresponding css class "estacio-[tipus]-logo" should be added in widgets.scss > .logo-estacio > &.estacio-[tipus]-logo
        hasMiniature: false, // If set to true, a minitrue image should be added in static/public/stations/ with the name [tipus]_miniature.jpg
    }
    static parametersDescription = addKeysAsField({
        ...GrufBus.parametersDescription,
        clipNumber: {type: 'int', min: 0, max: 3, initial: 0},
        presetID: {type: 'string', initial: 'init'},
        isRecordArmed:{type: 'bool', initial: false, local: true},
        fx_EQ_isActive: {type : 'bool', initial: false, bind: "preset"},
        fx_EQ_low:{type: 'float', unit: units.decibel, min: -12, max: 12, initial: 0.0, bind: "preset"},
        fx_EQ_mid:{type: 'float', unit: units.decibel, min: -12, max: 12, initial: 0.0, bind: "preset"},
        fx_EQ_high:{type: 'float', unit: units.decibel, min: -12, max: 12, initial: 0.0, bind: "preset"},
        send_reverbA: {type: 'float', unit: units.decibel, min: -60, max: 6, initial: -60, bind: "preset"},
        send_reverbB: {type: 'float', unit:units.decibel, min: -60, max: 6, initial: -60, bind: "preset"},
        send_delayA: {type: 'float', unit:units.decibel, min: -60, max: 6, initial: -60, bind: "preset"},
        send_delayB: {type: 'float', unit:units.decibel, min: -60, max: 6, initial: -60, bind: "preset"},
    }, "nom");
    static pitchRange = {
        usable: {min: 0, max: 127},
    }
    static numClips = 4;
    static defaultClipLength = 32;
    
    static makeDefaultName() {
        const sameTipusNumber = Object.values(staticSelect(selectInstruments)).filter(value => value.tipus === this.metadata.tipus).length;
        return `${this.metadata.tipusDisplay} ${sameTipusNumber + 1}`;
    }
    static createInitialStateObject() {
        return {
            tipus: this.metadata.tipus,
            versio: this.metadata.versio,
            name: this.makeDefaultName(),
            parameters: Object.fromEntries([...Object.entries(this.parametersDescription)]
            .filter(([_key, value]) => value.bind == null) // only unbound parameters
            .map(([key, value]) => [key, value.initial])),
            clips: Array(this.numClips).fill().map(() => ({
                sequence: this.metadata.tipus !== "looper" ? [] : undefined,
                length: this.defaultClipLength,
                sound: this.metadata.tipus === "looper" ? {} : undefined,
                _metadata: { instrumentType: this.metadata.tipus },
            })),
        }
    }
    // some getters for static fields
    get metadata() { return this.constructor.metadata }
    get parametersDescription() { return this.constructor.parametersDescription }
    get pitchRange() { return this.constructor.pitchRange }
    get defaultClipLength() { return this.constructor.defaultClipLength }

    static maxPolyphony = 6;
    
    audioNodes = {}
    volatileState = {}
    
    constructor(id) {
        this.id = id;
    }
    
    // Helper functions for parameters
    getParameterDescription = parameterName =>  this.parametersDescription[parameterName];
    isParameterBoundToPresets = parameterName => this.getParameterDescription(parameterName).bind === "preset";
    isParameterBoundToClips = parameterName => this.getParameterDescription(parameterName).bind === "clip";
    
    getInitPreset = () => Object.fromEntries(
        [...Object.entries(this.parametersDescription)
        .filter(([_parameterName, {bind}]) => bind === "preset")
            .map(([parameterName, {initial}]) => [parameterName, initial]),
            ["_metadata", { name: "Init", isFactory: true, instrumentType: this.metadata.tipus }],
        ])
    
    ///////////////////////
    // STORE: SELECTORS
    //////////////////////
    getStateSelector = () => state => state.project.instruments[this.id];
    getNameSelector = () => createSelector(this.getStateSelector(), selfState => selfState.name);
    getClipsSelector = () => createSelector(this.getStateSelector(), selfState => selfState.clips);
    
    _getParameterSelector(parameterName, {presetIDSelector, clipNumberSelector}={}) {
        if (this.getParameterDescription(parameterName) === undefined) {
            throw new Error(`Parameter ${parameterName} doesn't exist!`);
        }
        if (this.isParameterBoundToPresets(parameterName)) {
            if (presetIDSelector == null) throw new Error(`The parameter ${parameterName} needs a preset ID!`);
            return createSelector(selectPresets, presetIDSelector, (presets, presetID) => {
                const preset = (presetID === "init") ? this.getInitPreset() : presets[presetID];
                return preset[parameterName] ?? this.getParameterDescription(parameterName).initial;
            });
        }
        if (this.isParameterBoundToClips(parameterName)) {
            if (clipNumberSelector == null) throw new Error(`The parameter ${parameterName} needs a clip number!`);
            // TODO: ".sound" is hardcoded. would be nice to be able to drill parameters.
            return createSelector(this.getClipsSelector(), clipNumberSelector, (clips, clipNumber) => {
                const clip = clips[clipNumber];
                return clip.sound[parameterName] ?? this.getParameterDescription(parameterName).initial;
            })
        }
        return createSelector(this.getStateSelector(), selfState => selfState.parameters[parameterName]);
    }
    
    // Selectors: DISPLAY STUFF
    // todo: we should memoize this so that it doesn't recompute ever time it asks for it
    getDisplayParameterSelector = parameterName => {
        if (parameterName === "presetID" || parameterName === "clipNumber") return this._getParameterSelector(parameterName);
        return this._getParameterSelector(parameterName, {
            presetIDSelector: this.getDisplayParameterSelector("presetID"),
            clipNumberSelector: this.getDisplayParameterSelector("clipNumber")
        })
    }
    getDisplayClipSelector = () => createSelector(this.getDisplayParameterSelector("clipNumber"), this.getClipsSelector(), (clipNumber, clips) => clips[clipNumber]);
    getDisplaySequenceSelector = () => createSelector(this.getDisplayClipSelector(), clip => clip.sequence);
    getDisplayClipLengthSelector = () => createSelector(this.getDisplayClipSelector(), clip => clip.length);
    

    // Selectors: PLAYBACK STUFF
    getPlaybackParameterSelector = parameterName => {
        if (parameterName === "presetID" || parameterName === "clipNumber") {
            return createSelector(selectPlaybackMode, this.getDisplayParameterSelector(parameterName), selectlaunchedSlots, selectScenes, (playbackMode, displayParameter, launchedSlots, scenes) => {
                if (playbackMode === "followInstruments") return displayParameter;
                const scene = launchedSlots[this.id].currentScene;
                if (!scene) return undefined;
                return scenes[scene].slots[this.id][parameterName]; // either clipNumber or presetID
            })
        }
        return this._getParameterSelector(parameterName, {
            presetIDSelector: this.getPlaybackParameterSelector("presetID"),
            clipNumberSelector: this.getPlaybackParameterSelector("clipNumber")
        })
    }

    getPlaybackPresetSelector = () => createSelector(this.getPlaybackParameterSelector("presetID"), selectPresets, (presetID, presets) => {
        return (presetID === "init") ? this.getInitPreset() : presets[presetID];
    })
    getPlaybackClipSelector = () => createSelector(this.getPlaybackParameterSelector("clipNumber"), this.getClipsSelector(), (clipNumber, clips) => {
        return clips[clipNumber];
    })


    // staticSelect. should never be used inside of react
    getPlaybackClipNumber = state => staticSelect(this.getPlaybackParameterSelector("clipNumber"), state);
    getPlaybackPresetID = state => staticSelect(this.getPlaybackParameterSelector("presetID"), state);
    getPlaybackClip = state => staticSelect(this.getPlaybackClipSelector(), state);
    getPlaybackParameterValue = parameterName => staticSelect(this.getPlaybackParameterSelector(parameterName));


    _getParameterUpdater(parameterName, {presetID, clipNumber, load}={}) {
        const isLocal = this.getParameterDescription(parameterName).local;
        if (this.isParameterBoundToPresets(parameterName)) {
            return value => {
                const preset = presetID === "init" ? this.getInitPreset() : staticSelect(selectPresets)[presetID];
                if (!preset._metadata.isUnsaved) {
                    const newPresetID = makeUID("preset");
                    const newPreset = {...preset, [parameterName]: value, _metadata: {...preset._metadata, isUnsaved: true, isFactory: false}};
                    return dispatch => {
                        dispatch(makePreset({presetID: newPresetID, data: newPreset}));
                        if (load) dispatch(loadPresetInInstrument({instrumentID: this.id, presetID: newPresetID}));
                    }
                }
                return setPresetParameter({presetID, parameter: parameterName, value});
            }
        }
        if (this.isParameterBoundToClips(parameterName)) {
            return value => setClipParameter({instrumentID: this.id, clipNumber, parameter: parameterName, value, isLocal})
        }
        return value => setInstrumentParameter({instrumentID: this.id, parameter: parameterName, value, isLocal}); 
    }
    getDisplayParameterUpdater(parameterName) {
        return value => {
            const presetID = staticSelect(this.getDisplayParameterSelector("presetID"));
            const clipNumber = staticSelect(this.getDisplayParameterSelector("clipNumber"));
            return this._getParameterUpdater(parameterName, {presetID, clipNumber, load: true})(value);
        }
    }

    // TODO: Remove. Deprecated
    updateParametreInstrument = (nomParametre, valor) => store.dispatch(this.getDisplayParameterUpdater(nomParametre)(valor));

    createdParameterHandlers = new Map();
    getParameterHandler(parameterName) {
        if (!this.createdParameterHandlers.has(parameterName)) {
            const parameterHandler = new ParameterHandler(this.getParameterDescription(parameterName), this.getDisplayParameterSelector(parameterName), this.getDisplayParameterUpdater(parameterName));
            this.createdParameterHandlers.set(parameterName, parameterHandler);
        }
        return this.createdParameterHandlers.get(parameterName);
    }
    

    // todo: this should be a method of audioEngine
    getStepDuration = () => {
        return 60.0 / getAudioGraphInstance().getBpm() / 4.0;
    };

    // deprecated. todo: should dispatch directly from react!
    updateSequence(value) {
        const clipNumber = staticSelect(this.getDisplayParameterSelector("clipNumber"));
        return store.dispatch(setClipSequence({instrumentID: this.id, clipNumber, value}))
    }


    // STATE + AUDIO STUFF. KINDA MIXED WITH STORE'S AUDIOAPPLIER

    // Called when we want to update the whole audio graph from the state (for example, to force syncing with the state)
    applyStateToAudioGraph() {
        this.updateAudioGraphForUnboundParameters();
        this.updateAudioGraphForPlaybackPreset();
        this.updateAudioGraphForPlaybackClip();
    }

    updateAudioGraphForUnboundParameters() {
        Object.keys(this.parametersDescription).forEach(parameterName => {
            if (parameterName === "clipNumber") return;
            if (this.parametersDescription[parameterName].bind) return;
            const value = staticSelect(this.getPlaybackParameterSelector(parameterName));
            this.applyAudioGraphParameter(parameterName, value);
        })

    }

    updateAudioGraphForPlaybackPreset() {
        const preset = staticSelect(this.getPlaybackPresetSelector());
        if (preset == null) return; // todo: remove this return, could lead to more difficult debugging. probably it was an extra call from the store's launchScene or dequeueSlots middleware...
        Object.entries(preset).filter(([name, _v]) => name !== "_metadata").forEach(([name, value]) => {
            this.applyAudioGraphParameter(name, value);
        });
    }

    updateAudioGraphForPlaybackClip() {
        const clip = staticSelect(this.getPlaybackClipSelector());
        if (clip == null || clip.sound == null) return;
        Object.entries({...Sound.getDefaults(), ...clip.sound})
            .forEach(([name, value]) => this.applyAudioGraphParameter(name, value));
    }


    // Called when a parameter of an station's audio graph is updated
    applyAudioGraphParameter(nomParametre, value) {
        if (!getAudioGraphInstance().isGraphBuilt()) return;
        const match = this._setParameterInAudioGraph(nomParametre, value); // try to match with general parameters
        if (!match) this.setParameterInAudioGraph(nomParametre,value); // if not, go to individual implementations
    }

    updateMute() {
       const isAnySolo = staticSelect(selectIsAnyInstrumentSolo);
        if (this.muteChannel) {
            const isChannelSoloed = staticSelect(this.getDisplayParameterSelector('isSolo'));
            const isChannelIndirectMuted = isAnySolo && !isChannelSoloed;
            const isChannelDirectMuted = staticSelect(this.getDisplayParameterSelector('isMute'));
            this.muteChannel.mute = isChannelDirectMuted || isChannelIndirectMuted;
        }
    }

    unfinishedNotes = [];
    handlePianoRollRecording(pitch, midiVelocity=127, noteOff) {
        if (!staticSelect(this.getPlaybackParameterSelector('isRecordArmed'))) return;
        const currentMainSequencerStep = getAudioGraphInstance().getTransportCurrentStep();
        const currentStep = currentMainSequencerStep % this.getPlaybackClip().length;
        
        if (!noteOff) return this.unfinishedNotes.push({pitch, velocity: midiVelocity, onset: currentStep});
        
        const noteIndex = this.unfinishedNotes.findIndex(n => n.pitch === pitch);
        if (noteIndex === -1) return; // if there isn't any note like that, ignore
        const [note] = this.unfinishedNotes.splice(noteIndex, 1);
        
        if (note.onset > currentStep) return; // likewise, don't save the note if the noteoff insn't bigger than the noteon
        
        note.duration = currentStep === note.onset ? 1 : currentStep - note.onset;
        const jsPianoRollEl = document.getElementById(this.id + "_" + "piano-roll");
        note.id =  jsPianoRollEl.getNextAvailableID();

        const sequence = structuredClone(staticSelect(this.getDisplaySequenceSelector())); // we want it to be mutable so we clone it
        sequence.push(note);
        this.updateSequence(sequence); // and save change in server!
    }

    // AUDIO ONLY STUFF
    get masterChannel() {
        return this.bus.masterChannel;
    }

    get muteChannel() {
        return this.bus.muteChannel;
    }

    getCurrentLevel() {
        if (this.bus == null) return -60;
        return Math.max(...this.bus.getCurrentLevels());
    }

    _setParameterInAudioGraph(name, value) {
        if (name.startsWith('fx') || name.startsWith('send')) return this.setFxParameterInAudioGraph(name, value);

        // isSolo and isMute are handled separately in store audioApplier
        switch (name) {
            case 'gain':
                if (this.masterChannel !== undefined){
                    const volume = Tone.gainToDb(value);
                    this.masterChannel.volume.linearRampTo(volume, 0.01);
                }
                return true;
            case 'pan':
                if (this.masterChannel !== undefined){
                    const pan = value;
                    this.masterChannel.pan.linearRampTo(pan, 0.01);
                }
                return true;
            case 'presetID':
                if (staticSelect(selectPlaybackMode) === "followInstruments") this.updateAudioGraphForPlaybackPreset();
                return true;
            case 'clipNumber':
                if (staticSelect(selectPlaybackMode) === "followInstruments") this.updateAudioGraphForPlaybackClip();
                return true;
        }
        return false;
    }

    setParameterInAudioGraph(name, value) {
        // Called when a parameter gets updated and needs to update it's corresponding audio graph node
        // This needs to be implemented in each station
    }

    setFxParameterInAudioGraph(name, value) {
        if (this.audioNodes.hasOwnProperty('effects') === false){
            // If instrument has no effect nodes, don't try tu update anything
            return false;
        }

        const eqNode = this.audioNodes.effects['eq3'];
        
        // FX propis de l'instrument
        if (name == "fx_EQ_low") {
            eqNode.set({'low': value});
        } else if (name == "fx_EQ_mid"){
            eqNode.set({'mid': value});
        } else if (name == "fx_EQ_high"){
            eqNode.set({'high': value});
        } else if (name == "fx_EQ_isActive"){
            if (value) eqNode.activate();
            else eqNode.deactivate();
        } else if (name === "send_reverbA") {
            this.audioNodes.effects.reverbASendChannel.volume.value = value;
        } else if (name === "send_reverbB"){
            this.audioNodes.effects.reverbBSendChannel.volume.value = value;
        } else if (name === "send_delayA") {
            this.audioNodes.effects.delayASendChannel.volume.value = value;
        } else if (name === "send_delayB") {
            this.audioNodes.effects.delayBSendChannel.volume.value = value;
        } else return false;

        return true;
    }

    addEffectChainNodes (audioInput, audioOutput){

         const effects = {
            eq3: new FXWrapper(GrufEQ3, {postgain: Tone.dbToGain(3), isActive: staticSelect(this.getPlaybackParameterSelector("fx_EQ_isActive"))}, {Q: 10}),
            reverbASendChannel: new Tone.Channel({ 
                volume: -100 
            }),
            reverbBSendChannel: new Tone.Channel({ 
                volume: -100 
            }),
            delayASendChannel: new Tone.Channel({ 
                volume: -100 
            }),
            delayBSendChannel: new Tone.Channel({ 
                volume: -100 
            })
        }

        // Add the nodes to the station's audioNodes dictionary
        this.audioNodes.effects = effects;
        
        // Connect the nodes in the effect chain
        audioInput.chain(effects.eq3, audioOutput);

        // Connect global effect sends
        effects.reverbASendChannel.connect(getAudioGraphInstance().returns.reverbA)
        effects.reverbBSendChannel.connect(getAudioGraphInstance().returns.reverbB)
        effects.delayASendChannel.connect(getAudioGraphInstance().returns.delayA)
        effects.delayBSendChannel.connect(getAudioGraphInstance().returns.delayB);
        this.bus.connect(effects.reverbASendChannel);
        this.bus.connect(effects.reverbBSendChannel);
        this.bus.connect(effects.delayASendChannel);
        this.bus.connect(effects.delayBSendChannel);
    }


    async buildInstrumentAudioGraph() {
        this.bus = new GrufBus();
        // Use this method to build audio nodes and the like.
        // We make it "async" as there might be the need to load resources (e.g. WAM modules)
        // and wait until these are loaded to continue using "await"
        return {}
    }

    dispose() {
        this.onStopAllSounds()
        Tone.getContext()._timeouts.cancel(0);  // This afects all Tone nodes, but it's probably necessary to fully destroy the node 
        this.bus.dispose();
        for (const key of Object.keys(this.audioNodes)) {
            if (key == "effects") {
                for (const key2 of Object.keys(this.audioNodes.effects)) {
                    const node = this.audioNodes.effects[key2];
                    node.dispose();
                }
            } else {
                const node = this.audioNodes[key];
                node.dispose();
            }
        }
    }


    onTransportStop() {
        // Called when audio graph is stopped
    }

    onSequencerStep(currentMainSequencerStep, time) {
        // Called at each step (16th note) of the main sequencer so the station can trigger notes, etc.
        // You should use staticSelect(getPlaybackParameterSelector), instead of staticSelect(getDisplayParameterSelector), to account for presets, etc.
    }

    onMidiNote(pitch, midiVelocity, noteOff, extras) {
        // Called everytime a note message is received from a live stream of notes (could be MIDI input or virtual input
        // noteOff = boolean which will be true if the message is a noteOff
    }

    onStopAllSounds() {
        // Called when we want to immediately stop all sound produced by the station
    }

    adjustPitchToInstrumentRange(pitch) {
        return pitch;
    }

    // UI stuff
    getUserInterfaceComponent() {
        throw new Error("No UI per aquiesta instrument!");  // If not overriden, throw error
    }

}